import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pharmacyApi } from "@/api/pharmacy";
import { pharmacyPurchaseApi } from "@/api/pharmacyPurchase";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Pencil, Plus } from "lucide-react";

const VENDOR_INVOICE_EDITOR_ROLES = new Set(["PHARMACIST", "HOSPITAL_ADMIN", "SUPER_ADMIN"]);

const PAYMENT_METHODS = ["CASH", "UPI", "CARD", "BANK_TRANSFER"] as const;

type PharmacyVendorDetailPageProps = {
  basePath: string;
  adminMode?: boolean;
};

export default function PharmacyVendorDetailPage({
  basePath,
  adminMode = false,
}: PharmacyVendorDetailPageProps) {
  const { vendorId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const { user, activeHospitalId } = useAuth();
  const hospitalIdFromQuery = searchParams.get("hospitalId")?.trim() || undefined;
  const effectiveHospitalId = adminMode
    ? hospitalIdFromQuery || activeHospitalId || user?.hospitalId || undefined
    : undefined;
  const hospitalParams = effectiveHospitalId ? { hospitalId: effectiveHospitalId } : undefined;

  const listPath = adminMode ? `${basePath}/pharmacy-vendors` : `${basePath}/vendors`;

  const [payment, setPayment] = useState({ amount: "", method: "CASH", date: "", notes: "" });
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    invoiceNumber: "",
    invoiceDate: "",
    totalAmount: "",
    notes: "",
    file: null as File | null,
  });
  const [portalForm, setPortalForm] = useState({ email: "", password: "", name: "" });
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [editingInvoiceAmount, setEditingInvoiceAmount] = useState("");

  const canEditVendorInvoices = VENDOR_INVOICE_EDITOR_ROLES.has(user?.role ?? "");

  const { data: detail } = useQuery({
    queryKey: ["vendor-detail", vendorId, effectiveHospitalId ?? "self"],
    enabled: !!vendorId,
    queryFn: async () => {
      const res = await pharmacyApi.getVendorDetail(vendorId, hospitalParams);
      return (res.data as any)?.data;
    },
  });

  const { data: statement } = useQuery({
    queryKey: ["vendor-ledger-statement", vendorId, effectiveHospitalId ?? "self"],
    enabled: !!vendorId,
    queryFn: async () => {
      const res = await pharmacyApi.getVendorLedgerStatement(vendorId, hospitalParams);
      return (res.data as any)?.data;
    },
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["vendor-invoices", vendorId, effectiveHospitalId ?? "self"],
    enabled: !!vendorId,
    queryFn: async () => {
      const res = await pharmacyApi.getVendorInvoices(vendorId, hospitalParams);
      return (res.data as any)?.data ?? [];
    },
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["vendor-payments", vendorId, effectiveHospitalId ?? "self"],
    enabled: !!vendorId,
    queryFn: async () => {
      const res = await pharmacyApi.getVendorPayments(vendorId, hospitalParams);
      return (res.data as any)?.data ?? [];
    },
  });

  const invalidateAll = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["vendor-detail", vendorId] }),
      qc.invalidateQueries({ queryKey: ["vendor-ledger-statement", vendorId] }),
      qc.invalidateQueries({ queryKey: ["vendor-invoices", vendorId] }),
      qc.invalidateQueries({ queryKey: ["vendor-payments", vendorId] }),
      qc.invalidateQueries({ queryKey: ["pharmacy-vendors"] }),
    ]);
  };

  const createPaymentMutation = useMutation({
    mutationFn: async () => {
      const amt = parseFloat(payment.amount);
      if (!amt || amt <= 0 || isNaN(amt)) throw new Error("Amount must be > 0");
      if (!payment.method) throw new Error("Payment method required");
      return pharmacyApi.createVendorPayment(
        vendorId,
        {
          amount: amt,
          paymentMethod: payment.method,
          paymentDate: payment.date || undefined,
          notes: payment.notes.trim() || undefined,
        },
        hospitalParams
      );
    },
    onSuccess: async () => {
      toast.success("Payment recorded");
      setPayment({ amount: "", method: "CASH", date: "", notes: "" });
      await invalidateAll();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || err?.message || "Failed to record payment"),
  });

  const portalAccessMutation = useMutation({
    mutationFn: async () => {
      const email = (portalForm.email || v.email || "").trim();
      if (!email || portalForm.password.length < 6) {
        throw new Error("Email and password (min 6 chars) are required");
      }
      return pharmacyPurchaseApi.setupVendorPortal(vendorId, {
        email,
        password: portalForm.password,
        name: portalForm.name.trim() || undefined,
      });
    },
    onSuccess: async (res) => {
      const msg = (res?.data as { message?: string })?.message;
      toast.success(msg || "Vendor portal login configured");
      setPortalForm({ email: "", password: "", name: "" });
      await invalidateAll();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || err?.message || "Failed to set up portal"),
  });

  const updateInvoiceMutation = useMutation({
    mutationFn: async ({ invoiceId, totalAmount }: { invoiceId: string; totalAmount: number }) =>
      pharmacyApi.updateVendorInvoice(vendorId, invoiceId, { totalAmount }, hospitalParams),
    onSuccess: async () => {
      toast.success("Invoice amount updated");
      setEditingInvoiceId(null);
      setEditingInvoiceAmount("");
      await invalidateAll();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || err?.message || "Failed to update invoice"),
  });

  const createInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!invoiceForm.invoiceNumber.trim()) throw new Error("Invoice number is required");
      const amt = parseFloat(invoiceForm.totalAmount);
      if (!amt || amt <= 0 || isNaN(amt)) throw new Error("Total amount must be > 0");
      const fd = new FormData();
      fd.append("invoiceNumber", invoiceForm.invoiceNumber.trim());
      fd.append("totalAmount", String(amt));
      if (invoiceForm.invoiceDate) fd.append("invoiceDate", invoiceForm.invoiceDate);
      if (invoiceForm.notes.trim()) fd.append("notes", invoiceForm.notes.trim());
      if (invoiceForm.file) fd.append("file", invoiceForm.file);
      return pharmacyApi.createVendorInvoice(vendorId, fd, hospitalParams);
    },
    onSuccess: async () => {
      toast.success("Invoice added");
      setInvoiceOpen(false);
      setInvoiceForm({ invoiceNumber: "", invoiceDate: "", totalAmount: "", notes: "", file: null });
      await invalidateAll();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || err?.message || "Failed to add invoice"),
  });

  const invoiceRows = useMemo(() => invoices as any[], [invoices]);
  const statementRows = useMemo(() => (statement?.rows ?? []) as any[], [statement]);
  const ledger = detail?.ledger ?? statement?.ledger;

  if (!detail?.vendor) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">Loading vendor…</CardContent>
      </Card>
    );
  }

  const v = detail.vendor as any;

  return (
    <div className="space-y-4">
      <div>
        <Link to={listPath} className="inline-flex items-center text-sm text-blue-600 hover:underline">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to vendors
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{v.name}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div><span className="text-muted-foreground">Phone:</span> {v.phone}</div>
          <div><span className="text-muted-foreground">GST No:</span> {v.gstNo || "—"}</div>
          <div><span className="text-muted-foreground">Email:</span> {v.email || "—"}</div>
          <div className="md:col-span-3"><span className="text-muted-foreground">Address:</span> {v.address || "—"}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Supplier portal login</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {v.staffId
              ? "Portal login is active. Vendor signs in at the main THMS login to receive POs and submit invoices."
              : "Create credentials so this vendor can receive purchase orders and upload delivery invoices online."}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Login email</label>
              <Input
                type="email"
                value={portalForm.email || v.email || ""}
                onChange={(e) => setPortalForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="vendor@example.com"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                {v.staffId ? "New password (reset)" : "Password"}
              </label>
              <Input
                type="password"
                value={portalForm.password}
                onChange={(e) => setPortalForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Min 6 characters"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Display name (optional)</label>
              <Input
                value={portalForm.name}
                onChange={(e) => setPortalForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={v.name}
              />
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => portalAccessMutation.mutate()}
            disabled={portalAccessMutation.isPending}
          >
            {v.staffId ? "Update portal access" : "Enable portal access"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ledger summary</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div><span className="text-muted-foreground">Total invoices:</span> ₹{Number(ledger?.totalInvoices ?? 0).toFixed(2)}</div>
          <div><span className="text-muted-foreground">Total payments:</span> ₹{Number(ledger?.totalPayments ?? 0).toFixed(2)}</div>
          <div className="font-semibold"><span className="text-muted-foreground">Outstanding:</span> ₹{Number(ledger?.balance ?? 0).toFixed(2)}</div>
        </CardContent>
      </Card>

      <Tabs defaultValue="statement" className="w-full">
        <TabsList>
          <TabsTrigger value="statement">Statement</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="record">Record payment</TabsTrigger>
        </TabsList>

        <TabsContent value="statement" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Account statement</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Reference</th>
                      <th className="text-right p-2">Credit</th>
                      <th className="text-right p-2">Debit</th>
                      <th className="text-right p-2">Balance</th>
                      <th className="text-left p-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statementRows.length === 0 ? (
                      <tr><td colSpan={7} className="p-3 text-muted-foreground">No ledger entries yet</td></tr>
                    ) : (
                      statementRows.map((row) => (
                        <tr key={`${row.entryType}-${row.id}`} className="border-t">
                          <td className="p-2">{String(row.entryDate).slice(0, 10)}</td>
                          <td className="p-2">{row.entryType === "INVOICE" ? "Invoice" : "Payment"}</td>
                          <td className="p-2 font-medium">{row.reference}</td>
                          <td className="p-2 text-right">{row.credit > 0 ? `₹${Number(row.credit).toFixed(2)}` : "—"}</td>
                          <td className="p-2 text-right">{row.debit > 0 ? `₹${Number(row.debit).toFixed(2)}` : "—"}</td>
                          <td className="p-2 text-right font-medium">₹{Number(row.runningBalance).toFixed(2)}</td>
                          <td className="p-2 text-muted-foreground">{row.notes || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Purchase invoices</CardTitle>
              <Button onClick={() => setInvoiceOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add invoice
              </Button>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Invoice #</th>
                      <th className="text-right p-2">Manual total</th>
                      <th className="text-right p-2">Stock-in total</th>
                      <th className="text-right p-2">Gap</th>
                      <th className="text-left p-2">Upload</th>
                      {canEditVendorInvoices ? <th className="text-left p-2 w-24" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceRows.length === 0 ? (
                      <tr><td colSpan={canEditVendorInvoices ? 7 : 6} className="p-3 text-muted-foreground">No invoices yet</td></tr>
                    ) : (
                      invoiceRows.map((inv) => (
                        <tr key={inv.id} className="border-t">
                          <td className="p-2">{String(inv.invoiceDate).slice(0, 10)}</td>
                          <td className="p-2 font-medium">{inv.invoiceNumber}</td>
                          <td className="p-2 text-right">
                            {editingInvoiceId === inv.id ? (
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                className="h-8 w-28 ml-auto text-right"
                                value={editingInvoiceAmount}
                                onChange={(e) => setEditingInvoiceAmount(e.target.value)}
                              />
                            ) : (
                              <>₹{Number(inv.totalAmount ?? 0).toFixed(2)}</>
                            )}
                          </td>
                          <td className="p-2 text-right">₹{Number(inv.stockInTotal ?? 0).toFixed(2)}</td>
                          <td className={`p-2 text-right font-medium ${Number(inv.gap ?? 0) === 0 ? "" : Number(inv.gap ?? 0) > 0 ? "text-amber-600" : "text-rose-600"}`}>
                            ₹{Number(inv.gap ?? 0).toFixed(2)}
                          </td>
                          <td className="p-2">
                            {inv.fileUrl ? (
                              <a className="text-blue-600 hover:underline" href={inv.fileUrl} target="_blank" rel="noreferrer">View</a>
                            ) : "—"}
                          </td>
                          {canEditVendorInvoices ? (
                            <td className="p-2">
                              {editingInvoiceId === inv.id ? (
                                  <div className="flex flex-wrap gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={updateInvoiceMutation.isPending}
                                      onClick={() => {
                                        const amt = parseFloat(editingInvoiceAmount);
                                        if (!amt || amt <= 0 || isNaN(amt)) {
                                          toast.error("Enter a valid amount");
                                          return;
                                        }
                                        updateInvoiceMutation.mutate({ invoiceId: inv.id, totalAmount: amt });
                                      }}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingInvoiceId(null);
                                        setEditingInvoiceAmount("");
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 px-2"
                                    title="Edit manual invoice total"
                                    onClick={() => {
                                      setEditingInvoiceId(inv.id);
                                      setEditingInvoiceAmount(String(inv.totalAmount ?? ""));
                                    }}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                            </td>
                          ) : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Gap = manual invoice total minus stock-in lines for the same invoice number. Use Stock In / Purchase to enter line items.
                {canEditVendorInvoices
                  ? " Manual invoice totals can be edited anytime; outstanding updates from invoiced − paid."
                  : ""}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Payments</CardTitle></CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-right p-2">Amount</th>
                      <th className="text-left p-2">Mode</th>
                      <th className="text-left p-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(payments as any[]).length === 0 ? (
                      <tr><td colSpan={4} className="p-3 text-muted-foreground">No payments yet</td></tr>
                    ) : (
                      (payments as any[]).map((p) => (
                        <tr key={p.id} className="border-t">
                          <td className="p-2">{String(p.paymentDate).slice(0, 10)}</td>
                          <td className="p-2 text-right">₹{Number(p.amount).toFixed(2)}</td>
                          <td className="p-2">{String(p.paymentMethod).replace(/_/g, " ")}</td>
                          <td className="p-2 text-muted-foreground">{p.notes || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="record" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Record payment</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Amount *</label>
                  <Input type="number" min={0} step={0.01} value={payment.amount} onChange={(e) => setPayment((p) => ({ ...p, amount: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Mode *</label>
                  <Select value={payment.method} onValueChange={(val) => setPayment((p) => ({ ...p, method: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((m) => (
                        <SelectItem key={m} value={m}>{m.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Date</label>
                  <Input type="date" value={payment.date} onChange={(e) => setPayment((p) => ({ ...p, date: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Notes</label>
                <Textarea rows={2} value={payment.notes} onChange={(e) => setPayment((p) => ({ ...p, notes: e.target.value }))} />
              </div>
              <div className="flex justify-end">
                <Button onClick={() => createPaymentMutation.mutate()} disabled={createPaymentMutation.isPending}>
                  {createPaymentMutation.isPending ? "Saving…" : "Record payment"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Add invoice</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Invoice number *</label>
              <Input value={invoiceForm.invoiceNumber} onChange={(e) => setInvoiceForm((f) => ({ ...f, invoiceNumber: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Invoice date</label>
              <Input type="date" value={invoiceForm.invoiceDate} onChange={(e) => setInvoiceForm((f) => ({ ...f, invoiceDate: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Total amount *</label>
              <Input type="number" min={0} step={0.01} value={invoiceForm.totalAmount} onChange={(e) => setInvoiceForm((f) => ({ ...f, totalAmount: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Upload</label>
              <Input type="file" accept=".pdf,image/*" onChange={(e) => setInvoiceForm((f) => ({ ...f, file: e.target.files?.[0] ?? null }))} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Notes</label>
              <Textarea rows={2} value={invoiceForm.notes} onChange={(e) => setInvoiceForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceOpen(false)} disabled={createInvoiceMutation.isPending}>Cancel</Button>
            <Button onClick={() => createInvoiceMutation.mutate()} disabled={createInvoiceMutation.isPending}>
              {createInvoiceMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
